import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ListItemText, MenuItem, Collapse } from "@mui/material"
import { format, subDays, parseISO } from "date-fns"
import classNames from "classnames"

import BasicButton from "@/components/button/BasicButton"
import Icon from "@/components/icon/Icon"
import Selector from "@/components/form/selector/Selector"
import Checkbox from "@/components/form/checkbox/Checkbox"
import DatePicker from "@/components/form/datePicker/DatePicker"
import PlayerSelector from "@/app/report/components/filter/playerSelector/PlayerSelector"
import { CheckboxColor } from "@/types/components/checkbox"
import { ActivityTypeKey } from "@/constants/activity"
import { MatchDayType, ActivityParticipatedPlayerType } from "@/types/activity"
import { colorTokens } from "@/components/colorTokens"
import { useSeasonWeekQuery, usePlayersQuery } from "@/hooks/api/useSettingQuery"
import {
  PERIOD_OPTIONS,
  ACTIVITY_TYPE_OPTIONS,
  MATCH_DAY_OPTIONS,
} from "@/app/settings/report/filterPreset/constants"
import { PERIOD_LABELS } from "@/utils/filterPreset"
import type { FilterPresetPeriodType, UpsertFilterPresetPayload } from "@/types/filterPreset"

const NONE_SEASON_WEEK_ID = -1

/**
 * CSV 일괄 다운로드의 상세 필터 (Figma DES-507-csv-260727).
 * Filters 버튼으로 패널을 펼치고, 기간·타입·시즌·매치데이·선수를 좁힌다.
 * 필드 로직은 프리셋 에디터(ReportFilterFields)와 동일한 UpsertFilterPresetPayload 모델을 쓴다.
 * 선수 선택은 리포트 필터와 동일한 PlayerSelector 팝업을 Select 형 트리거로 연다.
 */
export default function CsvDetailedFilters({
  form,
  updateForm,
  expanded,
  onToggleExpanded,
  onTransitionEnd,
  selectedActivityIds,
}: {
  form: UpsertFilterPresetPayload
  updateForm: (updates: Partial<UpsertFilterPresetPayload>) => void
  /** 패널 펼침 = 상세 필터 적용 (접힘이면 전체 조회) */
  expanded: boolean
  onToggleExpanded: (expanded: boolean) => void
  /** 펼침/접힘 애니메이션이 끝난 뒤 호출 — 무거운 표 전환(조회 교체)을 이 시점으로 미뤄 애니메이션과 겹치지 않게 */
  onTransitionEnd: (expanded: boolean) => void
  /** PlayerSelector 의 그룹 전체선택 활성 조건용 (활동 미선택 시 비활성 — 리포트 필터와 동일) */
  selectedActivityIds: number[]
}) {
  const { t } = useTranslation()

  const { data: seasonWeekData } = useSeasonWeekQuery()
  const { data: players } = usePlayersQuery()

  const activeSeasonWeeks = useMemo(
    () => seasonWeekData?.seasonWeeks?.filter((sw) => sw.active) ?? [],
    [seasonWeekData],
  )
  const allSeasonWeekIds = useMemo(
    () => [...activeSeasonWeeks.map((sw) => sw.idfSeasonWeek as number), NONE_SEASON_WEEK_ID],
    [activeSeasonWeeks],
  )

  // --- 빈 배열이면 전체 선택으로 초기화 (프리셋 에디터와 동일 규칙) ---
  useEffect(() => {
    if (form.activityTypes.length === 0) {
      updateForm({ activityTypes: ACTIVITY_TYPE_OPTIONS.map((o) => o.value) })
    }
  }, [])

  useEffect(() => {
    if (allSeasonWeekIds.length > 0 && form.seasonWeekIds.length === 0) {
      updateForm({ seasonWeekIds: [...allSeasonWeekIds] })
    }
  }, [allSeasonWeekIds])

  useEffect(() => {
    if (form.matchDayTypes.length === 0) {
      updateForm({ matchDayTypes: [...MATCH_DAY_OPTIONS] })
    }
  }, [])

  useEffect(() => {
    if (players?.length && form.selectedPlayerIds.length === 0) {
      updateForm({ selectedPlayerIds: players.map((p) => p.idfPlayer) })
    }
  }, [players])

  // --- 선수 팝업 (리포트 필터 PlayerSelector 재사용 — 팀 선수를 참가자 타입으로 매핑) ---
  const [playerSelectorOpen, setPlayerSelectorOpen] = useState(false)
  const teamPlayers: ActivityParticipatedPlayerType[] = useMemo(
    () =>
      (players ?? []).map((p) => ({
        idfPlayer: p.idfPlayer,
        name: p.name,
        nickname: p.nickname,
        uniformNumber: p.uniformNumber,
        backNumber: p.uniformNumber,
        position: p.position,
        image: p.image,
        idfPlayerGroup: p.idfPlayerGroup ?? 0,
        playTime: 0,
      })),
    [players],
  )
  const selectedPlayers = useMemo(
    () => teamPlayers.filter((p) => form.selectedPlayerIds.includes(p.idfPlayer)),
    [teamPlayers, form.selectedPlayerIds],
  )
  const setSelectedPlayers: Dispatch<SetStateAction<ActivityParticipatedPlayerType[]>> = (
    action,
  ) => {
    const next = typeof action === "function" ? action(selectedPlayers) : action
    updateForm({ selectedPlayerIds: next.map((p) => p.idfPlayer) })
  }

  const playerValueLabel =
    teamPlayers.length > 0 && selectedPlayers.length === teamPlayers.length
      ? t("all")
      : `${selectedPlayers.length} ${t("selected", "Selected")}`

  // --- Reset: 기본값(All time + 모두 선택) ---
  const handleReset = () => {
    updateForm({
      periodType: "allTime",
      customDateStart: undefined,
      customDateEnd: undefined,
      activityTypes: ACTIVITY_TYPE_OPTIONS.map((o) => o.value),
      seasonWeekIds: [...allSeasonWeekIds],
      matchDayTypes: [...MATCH_DAY_OPTIONS],
      selectedPlayerIds: (players ?? []).map((p) => p.idfPlayer),
    })
  }

  const toggleActivityType = (key: ActivityTypeKey) => {
    const current = form.activityTypes
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    updateForm({ activityTypes: next })
  }
  const toggleAllActivityTypes = () => {
    if (form.activityTypes.length === ACTIVITY_TYPE_OPTIONS.length) {
      updateForm({ activityTypes: [] })
    } else {
      updateForm({ activityTypes: ACTIVITY_TYPE_OPTIONS.map((o) => o.value) })
    }
  }

  const toggleSeasonWeek = (id: number) => {
    const current = form.seasonWeekIds
    const next = current.includes(id) ? current.filter((i) => i !== id) : [...current, id]
    updateForm({ seasonWeekIds: next })
  }
  const toggleAllSeasonWeeks = () => {
    if (form.seasonWeekIds.length === allSeasonWeekIds.length) {
      updateForm({ seasonWeekIds: [] })
    } else {
      updateForm({ seasonWeekIds: [...allSeasonWeekIds] })
    }
  }

  const toggleMatchDay = (md: MatchDayType) => {
    const current = form.matchDayTypes
    const next = current.includes(md) ? current.filter((m) => m !== md) : [...current, md]
    updateForm({ matchDayTypes: next })
  }
  const toggleAllMatchDays = () => {
    if (form.matchDayTypes.length === MATCH_DAY_OPTIONS.length) {
      updateForm({ matchDayTypes: [] })
    } else {
      updateForm({ matchDayTypes: [...MATCH_DAY_OPTIONS] })
    }
  }

  const periodLabel = useMemo(() => {
    const opt = PERIOD_OPTIONS.find((o) => o.value === form.periodType)

    return opt ? t(opt.labelKey, PERIOD_LABELS[opt.value]) : ""
  }, [form.periodType, t])

  return (
    <>
      <div className="csv-export-filters-button-row">
        <BasicButton
          type="default-outlined"
          size="md"
          className={classNames({ open: expanded })}
          onClick={() => onToggleExpanded(!expanded)}>
          <Icon type="equalizer-line" size={20} />
          {t("filters", "Filters")}
          <Icon type={expanded ? "arrow-up" : "arrow-down"} size={20} color="currentColor" />
        </BasicButton>
      </div>

      {/* mountOnEnter 금지: 접힘 레이아웃(라벨 상단 18px)이 "닫힌 Collapse(높이 0) + flex gap 16"
          을 전제하므로, 최초 펼침 전에도 루트가 DOM 에 있어야 진입 직후 위치가 동일하다 */}
      <Collapse
        in={expanded}
        timeout={200}
        onEntered={() => onTransitionEnd(true)}
        onExited={() => onTransitionEnd(false)}>
        {/* 버튼 아래(36+16px)에서 패널이 시작하도록 하는 오프셋 — Collapse 가 이 여백까지 함께
            접었다 펴서, 여닫는 동안 패널이 우상단 고정 버튼과 겹치지 않는다 */}
        <div className="csv-export-filters-panel-offset">
          <div className="csv-export-filters-panel">
            <div className="csv-export-filters-panel-header">
              <p>{t("detailFilters", "Detailed Filters")}</p>
              <button type="button" className="csv-export-reset-link" onClick={handleReset}>
                {t("button.reset")}
              </button>
            </div>

            {/* Row 1: Period + Date Range 1:1 (Figma 12093:120621, custom 이 아니면 disabled) */}
            <div className="FilterPresetEditor__grid FilterPresetEditor__grid--2col">
              <div className="FilterPresetEditor__field">
                <label className="FilterPresetEditor__label">{t("period")}</label>
                <Selector
                  type="select"
                  value={form.periodType}
                  onChange={(e: any) => {
                    const nextType = e.target.value as FilterPresetPeriodType
                    const updates: Partial<UpsertFilterPresetPayload> = { periodType: nextType }
                    if (nextType === "custom" && (!form.customDateStart || !form.customDateEnd)) {
                      const today = new Date()
                      updates.customDateEnd = format(today, "yyyy-MM-dd")
                      updates.customDateStart = format(subDays(today, 6), "yyyy-MM-dd")
                    }
                    updateForm(updates)
                  }}
                  renderValue={() => periodLabel}
                  displayEmpty>
                  {PERIOD_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <ListItemText primary={t(opt.labelKey, PERIOD_LABELS[opt.value])} />
                    </MenuItem>
                  ))}
                </Selector>
              </div>

              <div className="FilterPresetEditor__field">
                <label className="FilterPresetEditor__label">{t("dateRange", "Date Range")}</label>
                <div className="FilterPresetEditor__date-range">
                  <DatePicker
                    value={
                      form.periodType === "custom" && form.customDateStart
                        ? parseISO(form.customDateStart)
                        : null
                    }
                    defaultValueNull
                    onChange={(date: Date | null) =>
                      updateForm({
                        customDateStart: date ? format(date, "yyyy-MM-dd") : undefined,
                      })
                    }
                    disabled={form.periodType !== "custom"}
                  />
                  <span>~</span>
                  <DatePicker
                    value={
                      form.periodType === "custom" && form.customDateEnd
                        ? parseISO(form.customDateEnd)
                        : null
                    }
                    defaultValueNull
                    onChange={(date: Date | null) =>
                      updateForm({
                        customDateEnd: date ? format(date, "yyyy-MM-dd") : undefined,
                      })
                    }
                    disabled={form.periodType !== "custom"}
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Activity Type / Season / Match Day / Player */}
            <div className="FilterPresetEditor__grid FilterPresetEditor__grid--4col">
              <div className="FilterPresetEditor__field">
                <label className="FilterPresetEditor__label">
                  {t("activityType", "Activity Type")}
                </label>
                <Selector
                  type="select"
                  multiple
                  displayEmpty
                  value={form.activityTypes}
                  onChange={(e: { target: { value: unknown } }) => e.target.value}
                  renderValue={() =>
                    form.activityTypes.length === ACTIVITY_TYPE_OPTIONS.length
                      ? t("all")
                      : `${form.activityTypes.length} ${t("selected", "selected")}`
                  }>
                  <MenuItem
                    key="all-activity-type"
                    onClick={toggleAllActivityTypes}
                    sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
                    <Checkbox
                      checked={form.activityTypes.length === ACTIVITY_TYPE_OPTIONS.length}
                      indeterminate={
                        form.activityTypes.length > 0 &&
                        form.activityTypes.length < ACTIVITY_TYPE_OPTIONS.length
                      }
                      size="medium"
                      color={CheckboxColor.PLATINUM}
                    />
                    <ListItemText primary={t("all")} />
                  </MenuItem>
                  {ACTIVITY_TYPE_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        toggleActivityType(opt.value)
                      }}>
                      <Checkbox checked={form.activityTypes.includes(opt.value)} size="medium" />
                      <ListItemText primary={t(opt.labelKey)} />
                    </MenuItem>
                  ))}
                </Selector>
              </div>

              <div className="FilterPresetEditor__field">
                <label className="FilterPresetEditor__label">{t("season", "Season")}</label>
                <Selector
                  type="select"
                  multiple
                  displayEmpty
                  value={form.seasonWeekIds.map(String)}
                  onChange={(e: { target: { value: unknown } }) => e.target.value}
                  renderValue={() =>
                    form.seasonWeekIds.length === allSeasonWeekIds.length
                      ? t("all")
                      : `${form.seasonWeekIds.length} ${t("selected", "selected")}`
                  }>
                  <MenuItem
                    key="all-season-week"
                    onClick={toggleAllSeasonWeeks}
                    sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
                    <Checkbox
                      checked={form.seasonWeekIds.length === allSeasonWeekIds.length}
                      indeterminate={
                        form.seasonWeekIds.length > 0 &&
                        form.seasonWeekIds.length < allSeasonWeekIds.length
                      }
                      size="medium"
                      color={CheckboxColor.PLATINUM}
                    />
                    <ListItemText primary={t("all")} />
                  </MenuItem>
                  {activeSeasonWeeks.map((sw, index) => (
                    <MenuItem
                      key={sw.idfSeasonWeek}
                      value={String(sw.idfSeasonWeek)}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        toggleSeasonWeek(sw.idfSeasonWeek as number)
                      }}>
                      <Checkbox
                        checked={form.seasonWeekIds.includes(sw.idfSeasonWeek as number)}
                        size="medium"
                      />
                      <ListItemText
                        primary={t("weekNumber", { number: index + 1 })}
                        secondary={
                          sw.startDate && sw.endDate ? `${sw.startDate} ~ ${sw.endDate}` : ""
                        }
                      />
                    </MenuItem>
                  ))}
                  <MenuItem
                    key="none"
                    value={String(NONE_SEASON_WEEK_ID)}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      toggleSeasonWeek(NONE_SEASON_WEEK_ID)
                    }}>
                    <Checkbox
                      checked={form.seasonWeekIds.includes(NONE_SEASON_WEEK_ID)}
                      size="medium"
                    />
                    <ListItemText primary={t("none", "None")} />
                  </MenuItem>
                </Selector>
              </div>

              <div className="FilterPresetEditor__field">
                <label className="FilterPresetEditor__label">{t("matchDay", "Match Day")}</label>
                <Selector
                  type="select"
                  multiple
                  displayEmpty
                  value={form.matchDayTypes}
                  onChange={(e: { target: { value: unknown } }) => e.target.value}
                  renderValue={() =>
                    form.matchDayTypes.length === MATCH_DAY_OPTIONS.length
                      ? t("all")
                      : `${form.matchDayTypes.length} ${t("selected", "selected")}`
                  }>
                  <MenuItem
                    key="all-match-day"
                    onClick={toggleAllMatchDays}
                    sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
                    <Checkbox
                      checked={form.matchDayTypes.length === MATCH_DAY_OPTIONS.length}
                      indeterminate={
                        form.matchDayTypes.length > 0 &&
                        form.matchDayTypes.length < MATCH_DAY_OPTIONS.length
                      }
                      size="medium"
                      color={CheckboxColor.PLATINUM}
                    />
                    <ListItemText primary={t("all")} />
                  </MenuItem>
                  {MATCH_DAY_OPTIONS.map((md) => (
                    <MenuItem
                      key={md}
                      value={md}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        toggleMatchDay(md)
                      }}>
                      <Checkbox checked={form.matchDayTypes.includes(md)} size="medium" />
                      <ListItemText primary={md} />
                    </MenuItem>
                  ))}
                </Selector>
              </div>

              <div className="FilterPresetEditor__field">
                <label className="FilterPresetEditor__label">{t("player")}</label>
                <PlayerSelector
                  open={playerSelectorOpen}
                  setOpen={setPlayerSelectorOpen}
                  selectedActivity={selectedActivityIds}
                  players={teamPlayers}
                  selectedPlayerList={selectedPlayers}
                  setSelectedPlayerList={setSelectedPlayers}
                  paramsVersion={false}
                  renderTrigger={({ ref, open, onToggle }) => (
                    <button
                      type="button"
                      ref={ref}
                      className="csv-export-player-trigger"
                      onClick={onToggle}>
                      <p>{playerValueLabel}</p>
                      <Icon
                        type={open ? "arrow-up" : "arrow-down"}
                        size={16}
                        color={colorTokens.field["ico-base"]}
                      />
                    </button>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </Collapse>
    </>
  )
}
